import React, { useRef, useEffect, useMemo } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { POI_Markers } from './POI_Markers'
import { POI_Markers_SEC006 } from './POI_Markers_SEC006'
import { SEC_Markers } from './SEC_Markers'
import { WeaponsSystemAnim } from './WeaponsSystemAnim'
import { Jet } from './Jet'
import useGame from '../stores/useGame.jsx'
import { useEncryptedGLTF } from '../../glb-encryption-utils/src/useEncryptedGLTF.js'

const ENCRYPTED_PATH = `/encrypted/${import.meta.env.VITE_GLB_ENCRYPTION_FILENAME}`
const ENCRYPTION_KEY = import.meta.env.VITE_GLB_ENCRYPTION_KEY

export function F35({ onBoneReady, ...props }) {
  const group = useRef()
  const pjdamGroupRef = useRef()

  const { gltf, loading, error } = useEncryptedGLTF({
    encryptedPath: ENCRYPTED_PATH,
    encryptionKey: ENCRYPTION_KEY,
    fallbackPath: '/F35_Gold.glb'
  })

  const nodes = useMemo(() => {
    if (!gltf?.scene) return null
    const map = {}
    gltf.scene.traverse(obj => { map[obj.name] = obj })
    return map
  }, [gltf])

  const materials = useMemo(() => {
    if (!gltf?.scene) return null
    const map = {}
    gltf.scene.traverse(obj => {
      if (!obj.material) return
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
      mats.forEach(mat => { if (mat?.name) map[mat.name] = mat })
    })
    return map
  }, [gltf])

  const animations = useMemo(() => gltf?.animations ?? null, [gltf])
  
  // Get PJDAM fire state for Jet visibility
  const pjdamFireActive = useGame((state) => state.pjdamFireActive)
  // Get selected section to control Internals mesh visibility/depth
  const selectedSec = useGame((state) => state.selectedSec)
  // During cinematic the animation clips own PJDAM_Solo_Ctrl — disable the lerp
  const cinematicMode = useGame((state) => state.cinematicMode)
  const setModelReady = useGame((state) => state.setModelReady)

  

  // Clamp glass reflection strength after load
  useEffect(() => {
    if (materials?.m_F35_Glass) {
      materials.m_F35_Glass.metalness = 1
      materials.m_F35_Glass.roughness = 0.15
      materials.m_F35_Glass.color = new THREE.Color(0xCCCCCC)
      materials.m_F35_Glass.needsUpdate = true
    }
  }, [materials])

  // Render Internals on top of all other geometry.
  useEffect(() => {
    if (materials?.Red_Hilight) {
      materials.Red_Hilight.depthTest = false
      materials.Red_Hilight.depthWrite = false
      materials.Red_Hilight.side = THREE.FrontSide
      materials.Red_Hilight.needsUpdate = true
    }
  }, [materials])

  // Apply AO texture to interior material
  useEffect(() => {
    if (!materials?.m_F35_Interior) return

    const loader = new THREE.TextureLoader()
    loader.load(
      '/textures/bomb_bay/F35_BombBay_m_F35_Interior_AO.png',
      (texture) => {
        texture.flipY = false
        if (materials?.m_F35_Interior) {
          materials.m_F35_Interior.aoMap = texture
          materials.m_F35_Interior.aoMapIntensity = 1
          materials.m_F35_Interior.needsUpdate = true
        }
      }
    )
  }, [materials])

  // Apply AO texture to PJDAM Green material
  useEffect(() => {
    if (!materials?.M_PJDAM_Green) return

    const loader = new THREE.TextureLoader()
    loader.load(
      '/textures/bomb_bay/Missile_M_PJDAM_Green_AO.png',
      (texture) => {
        texture.flipY = false
        if (materials?.M_PJDAM_Green) {
          materials.M_PJDAM_Green.aoMap = texture
          materials.M_PJDAM_Green.aoMapIntensity = 1
          materials.M_PJDAM_Green.needsUpdate = true
        }
      }
    )
  }, [materials])
  
  // Smoothly lower PJDAM group 1 unit and rotate 180° on X when SEC006 is active.
  // Disabled during cinematicMode so animation clips have full ownership of the group.
  const BASE_Y = -0.721
  useEffect(() => {
    // When cinematicMode ends, snap the group back to resting transform so the
    // useFrame lerp has a clean starting point and doesn't jump.
    if (!cinematicMode && pjdamGroupRef.current) {
      pjdamGroupRef.current.position.y = BASE_Y
      pjdamGroupRef.current.rotation.x = 0
    }
  }, [cinematicMode])

  useFrame((_, delta) => {
    if (!pjdamGroupRef.current) return
    if (cinematicMode) return // clips own the transform during cinematic
    const inWeaponStores = selectedSec === 'SEC006'
    const factor = 1 - Math.pow(0.1, delta)
    // Y: lower 1 unit in weapon stores
    const targetY = inWeaponStores ? BASE_Y - 1 : BASE_Y
    pjdamGroupRef.current.position.y = THREE.MathUtils.lerp(
      pjdamGroupRef.current.position.y,
      targetY,
      factor
    )
    // X rotation: flip 180° in weapon stores, return to 0 otherwise
    const targetRotX = inWeaponStores ? Math.PI : 0
    pjdamGroupRef.current.rotation.x = THREE.MathUtils.lerp(
      pjdamGroupRef.current.rotation.x,
      targetRotX,
      factor
    )
  })

  // Signal to the loading screen that the model is decrypted and ready
  useEffect(() => {
    if (gltf) setModelReady(true)
  }, [gltf])

  // Expose nodes.root to parent once resolved so scene-root VFX can read the bone
  useEffect(() => {
    if (nodes?.root && onBoneReady) onBoneReady(nodes.root)
  }, [nodes, onBoneReady])

  // Return nothing while the model is loading or if an error occurred,
  // or if the key skeleton nodes haven't been traversed yet
  if (loading || error || !nodes || !materials) return null
  if (!nodes['F-35-mesh'] || !nodes.root_F35 || !nodes.root) return null

  return (
    <group ref={group} {...props} dispose={null}>

      <POI_Markers nodes={nodes} />
      <POI_Markers_SEC006 nodes={nodes} />
      <SEC_Markers nodes={nodes} />

      
      {/* Weapons System Animation Controller */}
      <WeaponsSystemAnim animations={animations} modelRef={group} />

      <group name="Scene">
        <group name="F-35_armature">
          <group name="F-35">
            <skinnedMesh
              name="F-35-mesh"
              geometry={nodes['F-35-mesh'].geometry}
              material={materials.m_F35_Exterior}
              skeleton={nodes['F-35-mesh'].skeleton}
            />
            <skinnedMesh
              name="F-35-mesh_1"
              geometry={nodes['F-35-mesh_1'].geometry}
              material={materials.m_F35_Glass}
              skeleton={nodes['F-35-mesh_1'].skeleton}
            />
            <skinnedMesh
              name="F-35-mesh_2"
              geometry={nodes['F-35-mesh_2'].geometry}
              material={materials.m_F35_Nozzle}
              skeleton={nodes['F-35-mesh_2'].skeleton}
            />
            <skinnedMesh
              name="F-35-mesh_3"
              geometry={nodes['F-35-mesh_3'].geometry}
              material={materials.m_F35_Interior}
              skeleton={nodes['F-35-mesh_3'].skeleton}
            />
            <group name="ReleaseUnit" position={[2.3, -0.573, -0.804]} scale={1.117}>
              <mesh
                name="ReleaseUnit_mesh"
                castShadow
                receiveShadow
                geometry={nodes.ReleaseUnit_mesh.geometry}
                material={materials.M_ReleaseUnit_White}
              />
              <mesh
                name="ReleaseUnit_mesh_1"
                castShadow
                receiveShadow
                geometry={nodes.ReleaseUnit_mesh_1.geometry}
                material={materials.M_ReleaseUnit_Chrome}
              />
            </group>
          </group>
          <primitive object={nodes.root_F35} />
        </group>
        <group name="PJDAM_Solo_Ctrl" ref={pjdamGroupRef} position={[1.942, -0.721, -0.796]}>
          <mesh
            name="Internals"
            castShadow
            receiveShadow
            geometry={nodes.Internals.geometry}
            material={materials.Red_Hilight}
            position={[0.098, 0.012, -0.01]}
            rotation={[-0.018, 0, 0]}
            visible={selectedSec === 'SEC006'}
            renderOrder={999}
          />
          <group name="PJDAM_Fire_Ctrl">
            <group name="PJDAM_armature" position={[0, 0.012, -0.01]}>
              <group name="PJDAM_Gold">
              <skinnedMesh
                name="PJDAM_Gold"
                geometry={nodes.PJDAM_Gold.geometry}
                material={materials.M_PJDAM_Green}
                skeleton={nodes.PJDAM_Gold.skeleton}
              />
              <primitive object={nodes.root} />
              </group>
              <primitive object={nodes.root}>
                {/* Jet exhaust attached to PJDAM root bone — only visible during Animation 3 */}
                {pjdamFireActive && (
                  <group>
                    <Jet 
                      position={[0.0, -1.65, 0]} 
                      rotation={[0, 0.0, -Math.PI / 2 + 0.1]} 
                      scale={[3, 0.8, 1]}
                    />
                  </group>
                )}
              </primitive>
            </group>
          </group>
        </group>
        <group name="POI001" position={[2.14, -0.006, -2.392]} />
        <group name="POI002" position={[5.204, 0.963, 0]} />
        <group name="POI003" position={[4.941, -0.289, -0.99]} />
        <group name="POI004" position={[6.771, 0.681, 0]} />
        <group name="POI005" position={[-0.129, 0.152, 3.128]} />
        <group name="POI006" position={[-1.001, -0.686, -1.256]} />
        <group name="POI007" position={[6.807, -0.696, 0]} />
        <group name="POI008" position={[-4.197, -0.057, -0.003]} />
        <group name="POI009" position={[-1.055, 0.033, 0.014]} />
        <group name="POI010" position={[2.293, -0.479, -0.863]} />
        <group name="POI011" position={[-0.242, -0.917, 0.007]} />
        <group name="POI012" position={[5.177, -0.872, -0.004]} />
        <group name="POI013" position={[-3.839, 0.033, -1.657]} />
        <group name="POI014" position={[1.631, 0.781, 0.008]} />
        <group name="POI015" position={[1.607, -0.774, -0.808]} />
        <group name="POI016" position={[10, -0.774, 3]} />
        <group name="POI017" position={[2.6,  -0.668, -0.784]} />
        <group name="POI018" position={[2.3,  -0.668, -0.784]} />
        <group name="POI019" position={[2.0,  -0.668, -0.784]} />
        <group name="POI020" position={[1.7,  -0.668, -0.784]} />
        <group name="POI021" position={[1.4,  -0.668, -0.784]} />
        <group name="POI022" position={[1.1,  -0.668, -0.784]} />
        <group name="POI023" position={[2.6,  -0.668, -0.900]} />
        <group name="POI024" position={[2.3,  -0.668, -0.900]} />
        <group name="POI025" position={[2.0,  -0.668, -0.900]} />
        <group name="POI026" position={[1.7,  -0.668, -0.900]} />
        <group name="POI027" position={[1.4,  -0.668, -0.900]} />
        <group name="SEC001" position={[2.02, -2.299, 0]} />
        <group name="SEC002" position={[8.717, 2.013, 0]} />
        <group name="SEC003" position={[-3.037, 1.49, 0]} />
        <group name="SEC004" position={[2.434, 2.368, 0]} />
        <group name="SEC005" position={[8, -1.3, -0.863]} />
        <group name="SEC006" position={[1.607, -1, -0.808]} />
        <group name="POS001" position={[3.665, -3.203, -3.592]} />
        <group name="POS002" position={[14.552, 3.566, -5.006]} />
        <group name="POS003" position={[-11.029, 5.359, -4.768]} />
        <group name="POS004" position={[16.049, -5.82, 7.032]} />
        <group name="POS006" position={[1.6, -1.75, -2.0]} />
        <group name="POS005" position={[10, -5, 5]} />
        <group name="TAR001" position={[2.264, -0.676, -0.812]} />
        <group name="TAR002" position={[5, 0.07, 0]} />
        <group name="TAR003" position={[-2.544, 0.012, 0]} />
        <group name="TAR004" position={[1.673, 0.832, 0]} />
        <group name="TAR006" position={[1.6, -1.725, -0.796]} />
        <group name="TAR005" position={[2.6, -0.5, 0]} />
      </group>
    </group>
  )
}


